import {ProjectId} from '@webmate-web-core/shared/types/ids';

/**
 * Property the hosting workbench stamps on its own `window` so other tabs can verify that a
 * browsing context resolved by name really is that workbench, and not a blank tab the browser
 * just created for them.
 */
export const WORKBENCH_HOST_MARKER = '__wmWorkbenchHostProjectId';

export interface WorkbenchHostWindow extends Window {
    [WORKBENCH_HOST_MARKER]?: ProjectId;
}

/**
 * Browsing-context name of the workbench tab for a project. Both the hosting workbench (which sets
 * `window.name`) and every requester (which resolves that name) must derive it identically.
 */
export function workbenchWindowName(projectId: ProjectId): string {
    return `wm-workbench-${projectId}`;
}

export type WorkbenchFocusOutcome = 'focused' | 'unreachable' | 'blocked';

export interface WorkbenchFocusResult {
    outcome: WorkbenchFocusOutcome;
    /** Set only for `focused`: a verified handle on the hosting workbench window. */
    window?: Window;
}

export function markWorkbenchHostWindow(target: Window, projectId: ProjectId): void {
    (target as WorkbenchHostWindow)[WORKBENCH_HOST_MARKER] = projectId;
}

export function unmarkWorkbenchHostWindow(target: Window): void {
    delete (target as WorkbenchHostWindow)[WORKBENCH_HOST_MARKER];
}

/**
 * Brings the workbench tab for `projectId` to the foreground, without navigating it and without
 * ever leaving a stray tab behind.
 *
 * `window.open(url, name)` resolves `name` only within the caller's browsing-context group, so a tab
 * that did not itself open the workbench (a webmate tab started from a bookmark or a fresh browser
 * tab) cannot reach it — the browser silently creates a *new* context under that name instead. We
 * therefore probe with an empty URL, which never navigates a reused context, and check the marker
 * the host stamps on itself. An unmarked, still-blank context is one we just created: close it and
 * report `unreachable` rather than letting it boot into a duplicate workbench.
 *
 * There is no web API that focuses a tab in another browsing-context group, so `unreachable` is a
 * genuine dead end for the caller — it should tell the user instead of retrying.
 */
export function focusWorkbenchWindowByName(opener: Window, projectId: ProjectId, windowName?: string): WorkbenchFocusResult {
    const name = windowName || workbenchWindowName(projectId);
    const candidate = opener.open('', name) as WorkbenchHostWindow | null;
    if (!candidate) {
        // Popup blocker, or no user activation left on this document.
        return {outcome: 'blocked'};
    }
    if (candidate[WORKBENCH_HOST_MARKER] === projectId) {
        candidate.focus();
        return {outcome: 'focused', window: candidate};
    }
    if (isFreshlyOpenedContext(candidate)) {
        candidate.close();
    }
    // Anything unmarked but already showing a document is not ours to close.
    return {outcome: 'unreachable'};
}

function isFreshlyOpenedContext(candidate: Window): boolean {
    try {
        return candidate.location.href === 'about:blank';
    } catch {
        // Cross-origin context under this name — not a workbench, and not ours to touch.
        return false;
    }
}
